import React, {ReactNode, useMemo} from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {Box, IconButton, Tooltip, Typography} from '@mui/material'
import {TooltipProps} from '@mui/material/Tooltip'
import {IconButtonProps} from '@mui/material/IconButton'
import {SxProps, Theme} from '@mui/material/styles'

import styles from './style'

type TableTooltipProps = {
  columns: ReactNode[]
  rows: ReactNode[][]
  title?: ReactNode
  icon?: ReactNode
  tooltipProps?: Omit<TooltipProps, 'title' | 'children'>
  iconButtonProps?: IconButtonProps
}

const stringLength = (value: ReactNode): number => {
  if (value === null || value === undefined) {
    return 0
  }
  if (typeof value === 'number' || typeof value === 'bigint') {
    return value.toString().length
  }
  if (typeof value === 'string') {
    return value.length
  }
  return 12
}

const TableTooltip: React.FC<TableTooltipProps> = ({
  columns,
  rows,
  title,
  icon = <InfoOutlinedIcon fontSize="small" />,
  tooltipProps,
  iconButtonProps,
}) => {
  const columnCharacterWidths = useMemo(() => {
    return columns.map((column, columnIndex) => {
      const lengths = [column, ...rows.map(row => row[columnIndex])].map(stringLength)
      const maxLength = lengths.length ? Math.max(...lengths) : 0
      return maxLength
    })
  }, [columns, rows])

  const {componentsProps: providedComponentsProps, ...restTooltipProps} = tooltipProps ?? {}

  const mergedTooltipComponentsProps = useMemo(() => {
    if (!providedComponentsProps) {
      return {
        tooltip: {
          sx: styles.tooltip,
        },
      }
    }

    const {tooltip: providedTooltip, ...restComponentsProps} = providedComponentsProps
    const mergedSxSource = providedTooltip?.sx
    let mergedSx: SxProps<Theme> = styles.tooltip

    if (mergedSxSource) {
      mergedSx = Array.isArray(mergedSxSource)
        ? [styles.tooltip, ...mergedSxSource]
        : [styles.tooltip, mergedSxSource]
    }

    return {
      ...restComponentsProps,
      tooltip: {
        ...providedTooltip,
        sx: mergedSx,
      },
    }
  }, [providedComponentsProps]) as TooltipProps['componentsProps']

  const {'aria-label': ariaLabelProp, ...restIconButtonProps} = iconButtonProps ?? {}
  const defaultAriaLabel =
    ariaLabelProp ?? (typeof title === 'string' ? title : 'Open table tooltip')

  return (
    <Tooltip
      arrow
      placement="bottom-start"
      {...restTooltipProps}
      componentsProps={mergedTooltipComponentsProps}
      title={
        <Box>
          {title &&
            (typeof title === 'string' || typeof title === 'number' ? (
              <Typography variant="subtitle1" sx={styles.title}>
                {title}
              </Typography>
            ) : (
              <Box sx={styles.title}>{title}</Box>
            ))}
          <Box component="table" sx={styles.table}>
            <Box component="thead">
              <Box component="tr" sx={styles.headerRow}>
                {columns.map((column, columnIndex) => (
                  <Box
                    component="th"
                    key={`column-${columnIndex}`}
                    sx={styles.headerCell(columnCharacterWidths[columnIndex])}>
                    {column}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody" sx={styles.body}>
              {rows.map((row, rowIndex) => (
                <Box component="tr" key={`row-${rowIndex}`} sx={styles.bodyRow}>
                  {row.map((cell, cellIndex) => (
                    <Box
                      component="td"
                      key={`row-${rowIndex}-cell-${cellIndex}`}
                      sx={styles.bodyCell(columnCharacterWidths[cellIndex])}>
                      {cell}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      }>
      <Box sx={styles.wrapper}>
        <IconButton size="small" aria-label={defaultAriaLabel} {...restIconButtonProps}>
          {icon}
        </IconButton>
      </Box>
    </Tooltip>
  )
}

export default TableTooltip
